import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Form, Button, Spinner, Table } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  RegisterTaskReport,
  get_task_participants,
} from "../../../services/employeeApi.js";
import { toast } from "react-toastify";
import { FaStar } from "react-icons/fa";
import { useLocation } from "react-router-dom";
import { GetUserInfo } from "../../../utils/permissionChecker";

const RegisterTaskReportForm = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const user = GetUserInfo();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split("T")[0],
  });
  const taskData = location.state?.taskData;
  const taskId = location.state?.taskData?.task_id;
  const userBranchId = location.state?.taskData?.branch_id;
  const queryClient = useQueryClient();

  const registrationMutation = useMutation(async (newData) => {
    const response = await RegisterTaskReport(newData);
    queryClient.invalidateQueries("get-task-approval-requests");
    return response;
  });

  const { data, isLoading, isError, error } = useQuery(
    ["participantList", taskId],
    async () => {
      const [participantList] = await Promise.all([
        get_task_participants(taskId),
      ]);
      return { participantList };
    }
  );

  // Move the initialization logic here, before any conditional returns
  const branchEmployee = data?.participantList?.employees
    ? data.participantList.employees.filter(
        (emp) => emp.branch_id === userBranchId
      )
    : [];
  const initialEmployeeInputs = branchEmployee.reduce((acc, emp) => {
    acc[emp.employee_id] = { quantity: "", rating: 0 };
    return acc;
  }, {});

  useEffect(() => {
    if (branchEmployee.length > 0) {
      const initial = branchEmployee.reduce((acc, emp) => {
        acc[emp.employee_id] = { quantity: "", amount_birr: "", rating: 0 };
        return acc;
      }, {});
      setEmployeeInputs(initial);
    }
  }, [data]);
  const [employeeInputs, setEmployeeInputs] = useState(initialEmployeeInputs);
  const handleInputChange = (e, empId, field) => {
    const value = e.target.value;
    setEmployeeInputs((prev) => ({
      ...prev,
      [empId]: {
        ...prev[empId],
        [field]: value,
      },
    }));
  };

  const handleStarClick = (empId, ratingValue) => {
    setEmployeeInputs((prev) => ({
      ...prev,
      [empId]: {
        ...prev[empId],
        rating: ratingValue,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const incomplete = branchEmployee.filter((emp) => {
      const inputs = employeeInputs[emp.employee_id];
      return !inputs || inputs.quantity === "" || inputs.rating === 0;
    });

    if (incomplete.length > 0) {
      toast.error("Please fill Quantity and Rating for all employees.", {
        position: "top-right",
      });
      return; // STOP submission
    }
    try {
      setIsSubmitting(true);
      const records = branchEmployee.map((emp) => ({
        employee_id: emp.employee_id,
        task_id: taskId,
        date: formData.date,
        status: "approved",
        branch_id: emp.branch_id,
        quantity: Number(employeeInputs[emp.employee_id].quantity || 0),
        rating: employeeInputs[emp.employee_id].rating || 0,
      }));
      const payload = { records };
      const result = await registrationMutation.mutateAsync(payload);
      if (result.status == "success") {
        toast.success(
          `${result.created} record(s) created, ${result.skipped} skipped.`,
          { position: "top-right" }
        );
        history.push(`/task-approval-request`);
      } else {
        toast.error("Something went wrong while recording tasks.", {
          position: "top-right",
        });
      }
    } catch (error) {
      console.error("❌ Error:", error);
      toast.error(error.message || "Failed to record task report", {
        position: "top-right",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isError) {
    return <div>Error: {error.message}</div>;
  }

  return (
    <Form onSubmit={handleSubmit}>
      <div className="row">
        <div className="col-md-2">
          <Form.Label className="fw-bold text-muted">{t("Date")}</Form.Label>
        </div>
        <div className="col-md-4">
          <Form.Control
            type="date"
            required
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            max={new Date().toISOString().split("T")[0]}
          />
        </div>
      </div>
      <br></br>
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>#</th>
            <th>{t("Employee Name")}</th>
            <th>{t("Quantity")}</th>
            <th>{t("Rating")}</th>
          </tr>
        </thead>
        <tbody>
          {branchEmployee.map((emp, index) => (
            <tr key={emp.employee_id}>
              <td>{index + 1}</td>
              <td className="fw-semibold">{emp.full_name}</td>
              <td>
                <Form.Control
                  type="number"
                  min="0"
                  placeholder="Enter Quantity"
                  value={employeeInputs[emp.employee_id]?.quantity || ""}
                  onChange={(e) =>
                    handleInputChange(e, emp.employee_id, "quantity")
                  }
                  style={{ width: "150px" }}
                />
              </td>

              <td>
                {[1, 2, 3, 4, 5].map((star) => (
                  <FaStar
                    key={star}
                    size={25}
                    onClick={() => handleStarClick(emp.employee_id, star)}
                    color={
                      (employeeInputs[emp.employee_id]?.rating || 0) >= star
                        ? "#ffc107"
                        : "#e4e5e9"
                    }
                    style={{ cursor: "pointer", marginRight: 5 }}
                  />
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div className="d-flex mt-3 justify-content-end">
        <Button
          className="me-2 btn-secondary"
          disabled={isSubmitting}
          onClick={() => {
            history.push(`/my-tasks`);
          }}
        >
          {t("Cancel")}
        </Button>
        &nbsp; &nbsp; &nbsp;
        <Button className="btn-info" type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Spinner
                as="span"
                animation="grow"
                size="sm"
                role="status"
                aria-hidden="true"
              />{" "}
              {t("Submitting...")}
            </>
          ) : (
            t("Register")
          )}
        </Button>
      </div>
    </Form>
  );
};

export default RegisterTaskReportForm;
